// src/features/stream/lens.ts
/**
 * .tuMetrics {"tu1": 0.92, "tu2": 0.93, "tu3": 3, "notes": "Lenses: project KEY, board:user, #tag, room:…, service:type, inventory:type", "tuVersion": "2.5"}
 * .help "Defines the lenses (today, mine, quiet, project, board, tag, rooms, service streams, type inventories) that filter the stream, and converts them to and from the URL."
 */
import { create } from 'zustand';

export type RoomStreamKind = 'team' | 'dm' | 'standup' | 'company';

/** Pin-all corpus lists (formerly ephemeral VIEW). */
export type InventoryKind =
  | 'projects'
  | 'features'
  | 'cards'
  | 'sprints'
  | 'roadmaps'
  | 'plans'
  | 'meetings';

export const INVENTORY_KINDS: InventoryKind[] = [
  'projects',
  'features',
  'cards',
  'sprints',
  'roadmaps',
  'plans',
  'meetings',
];

export type Lens =
  | { kind: 'all' }
  | { kind: 'today' }
  | { kind: 'mine' }
  | { kind: 'quiet' }
  | { kind: 'project'; id: string }
  | { kind: 'person'; id: string }
  | { kind: 'tag'; tag: string }
  | { kind: 'sprint'; id: string }
  | { kind: 'roadmap'; id: string }
  | { kind: 'plan'; id: string }
  | { kind: 'service'; id: string }
  | { kind: 'guides' }
  | { kind: 'shares' }
  | { kind: 'inventory'; id: InventoryKind }
  | { kind: 'room'; streamType: RoomStreamKind; id: string };

export const ALL: Lens = { kind: 'all' };

/** Serialize for `/?lens=...`. Human-readable on purpose. */
export function serializeLens(lens: Lens): string {
  switch (lens.kind) {
    case 'all': return 'all';
    case 'today': return 'today';
    case 'mine': return 'mine';
    case 'quiet': return 'quiet';
    case 'project': return `project:${lens.id}`;
    case 'person': return `board:${lens.id}`;
    case 'tag': return `#${lens.tag}`;
    case 'sprint': return `sprint:${lens.id}`;
    case 'roadmap': return `roadmap:${lens.id}`;
    case 'plan': return `plan:${lens.id}`;
    case 'service': return `service:${lens.id}`;
    case 'guides': return 'guides';
    case 'shares': return 'shares';
    case 'inventory': return `inventory:${lens.id}`;
    case 'room':
      if (lens.streamType === 'company') return 'room:company';
      return `room:${lens.streamType}:${lens.id}`;
  }
}

function isInventoryKind(value: string): value is InventoryKind {
  return (INVENTORY_KINDS as string[]).includes(value);
}

export function parseLens(raw: string | null | undefined): Lens {
  if (!raw) return ALL;
  const value = raw.trim();
  if (value === 'today' || value === 'mine' || value === 'quiet' || value === 'all'
    || value === 'guides' || value === 'shares') {
    return { kind: value };
  }
  if (value.startsWith('inventory:')) {
    const id = value.slice('inventory:'.length);
    if (isInventoryKind(id)) return { kind: 'inventory', id };
    return ALL;
  }
  if (value.startsWith('project:')) return { kind: 'project', id: value.slice('project:'.length) };
  if (value.startsWith('board:')) return { kind: 'person', id: value.slice('board:'.length) };
  if (value.startsWith('sprint:')) return { kind: 'sprint', id: value.slice('sprint:'.length) };
  if (value.startsWith('roadmap:')) return { kind: 'roadmap', id: value.slice('roadmap:'.length) };
  if (value.startsWith('plan:')) return { kind: 'plan', id: value.slice('plan:'.length) };
  if (value.startsWith('service:')) return { kind: 'service', id: value.slice('service:'.length) };
  // Legacy: @uuid was person board before @ meant chat.
  if (value.startsWith('@')) return { kind: 'person', id: value.slice(1) };
  if (value === 'room:company') {
    return { kind: 'room', streamType: 'company', id: '' };
  }
  if (value.startsWith('room:')) {
    const rest = value.slice('room:'.length);
    const i = rest.indexOf(':');
    if (i > 0) {
      const streamType = rest.slice(0, i) as RoomStreamKind;
      const id = rest.slice(i + 1);
      if (streamType === 'team' || streamType === 'dm' || streamType === 'standup') {
        return { kind: 'room', streamType, id };
      }
    }
  }
  if (value.startsWith('#')) return { kind: 'tag', tag: value.slice(1).toLowerCase() };
  return ALL;
}

export function lensEquals(a: Lens, b: Lens): boolean {
  return serializeLens(a) === serializeLens(b);
}

/** Deep-link into the stream. `all` is bare `/` so the address bar stays clean. */
export function twoHrefFromLens(lens: Lens): string {
  const raw = serializeLens(lens);
  if (raw === 'all') return '/';
  return `/?lens=${encodeURIComponent(raw)}`;
}

interface LensState {
  lens: Lens;
  /** Last non-room lens — restore when parking a center room into the chat gutter. */
  lastWorkLens: Lens;
  setLens: (lens: Lens) => void;
}

const useLensStore = create<LensState>((set) => ({
  lens: ALL,
  lastWorkLens: ALL,
  setLens: (lens) =>
    set((state) => ({
      lens,
      lastWorkLens: lens.kind === 'room' ? state.lastWorkLens : lens,
    })),
}));

export default useLensStore;
